import { type Question, Serializer } from "survey-core";

/**
 * A `pillar_choice_dropdown` can load its options from a custom report instead
 * of a society choice category, configured through the four properties below.
 *
 * A composite question that embeds one - the degree picker, the profile address
 * block - hides that dropdown inside its content, where the survey creator's
 * Property Grid cannot reach it: the grid only ever edits the properties
 * registered on the composite's own class. SurveyJS has no way to forward a
 * nested element's properties automatically, so a composite has to surface them
 * itself. This module is that pass-through, so surfacing them is two calls
 * rather than a re-implementation per question:
 *
 * - `registerPillarChoiceCustomReportProperties` in the composite's `onInit`,
 *   which adds `<prefix>CustomReportId` and friends to the composite.
 * - `applyPillarChoiceCustomReportProperties` in its `onAfterRender`, which
 *   copies whatever the admin configured down onto the nested dropdown(s).
 */

/** The properties a `pillar_choice_dropdown` reads its report config from. */
const DROPDOWN_PROPERTIES = {
  reportId: "custom_report_id",
  labelPath: "custom_report_label_path",
  valuePath: "custom_report_value_path",
  filters: "custom_report_filters",
} as const;

/** Serializer class behind a dropdown's "Filter Options" rows. */
export const PILLAR_CHOICE_REPORT_FILTER_CLASS = "pillarchoicereportfilter";

export type CustomReportFilterRow = {
  filterKey?: string;
  questionName?: string;
};

/**
 * Registers the filter row class, once per page load. Both the dropdown itself
 * and every composite passing the properties through declare a property of this
 * type, and whichever registers first wins - the others must not re-register it
 * and drop the rows a form was already loaded with.
 */
export const registerPillarChoiceReportFilterClass = () => {
  if (Serializer.findClass(PILLAR_CHOICE_REPORT_FILTER_CLASS)) {
    return;
  }

  Serializer.addClass(
    PILLAR_CHOICE_REPORT_FILTER_CLASS,
    [
      { name: "filterKey", type: "string", isRequired: true },
      { name: "questionName", type: "string", isRequired: true },
    ],
    undefined,
    "itemvalue",
  );
};

/** One nested `pillar_choice_dropdown` a composite exposes the config for. */
export type PillarChoiceCustomReportField = {
  /** Name of the dropdown inside the composite's content. */
  elementName: string;
  /**
   * Prefix for the properties added to the composite, so a question holding
   * two dropdowns gets two independent sets. Defaults to `elementName`.
   */
  propertyPrefix?: string;
  /** Property Grid category the set is grouped under. */
  category: string;
};

type PillarChoiceCustomReportPropertyNames = {
  reportId: string;
  labelPath: string;
  valuePath: string;
  filters: string;
};

const pillarChoiceCustomReportPropertyNames = (
  field: PillarChoiceCustomReportField,
): PillarChoiceCustomReportPropertyNames => {
  const prefix = field.propertyPrefix ?? field.elementName;

  return {
    reportId: `${prefix}CustomReportId`,
    labelPath: `${prefix}CustomReportLabelPath`,
    valuePath: `${prefix}CustomReportValuePath`,
    filters: `${prefix}CustomReportFilters`,
  };
};

/**
 * A composite registers its properties in `onInit`, which runs once per page
 * load - but a question registered from more than one entry point (the creator
 * and the form preview both register the degree picker) would otherwise add the
 * same property twice and have it drawn twice in the Property Grid.
 */
const addPropertyOnce = (
  questionType: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  property: { name: string } & Record<string, any>,
) => {
  if (Serializer.findProperty(questionType, property.name)) {
    return;
  }

  Serializer.addProperty(questionType, property);
};

/**
 * Adds a "Custom Report ID"/"Label Path"/"Value Path"/"Filter Options" set to
 * `questionType` for each nested dropdown, mirroring what the dropdown shows on
 * its own. Call from the composite's `onInit`.
 */
export const registerPillarChoiceCustomReportProperties = ({
  questionType,
  fields,
}: {
  questionType: string;
  fields: PillarChoiceCustomReportField[];
}) => {
  // The filter rows property below is typed by this class, so it has to exist
  // whether or not the dropdown itself has been registered yet.
  registerPillarChoiceReportFilterClass();

  for (const field of fields) {
    const names = pillarChoiceCustomReportPropertyNames(field);

    // The paths and filters only mean anything against a report, so they stay
    // hidden until one is named - the same rule the dropdown's own grid uses.
    const hasReportId = (obj: Question) =>
      !!obj?.getPropertyValue(names.reportId);

    addPropertyOnce(questionType, {
      name: names.reportId,
      displayName: "Custom Report ID",
      type: "number",
      category: field.category,
    });

    addPropertyOnce(questionType, {
      name: names.labelPath,
      displayName: "Label Path",
      type: "string",
      category: field.category,
      visibleIf: hasReportId,
    });

    addPropertyOnce(questionType, {
      name: names.valuePath,
      displayName: "Value Path",
      type: "string",
      category: field.category,
      visibleIf: hasReportId,
    });

    addPropertyOnce(questionType, {
      name: names.filters,
      displayName: "Filter Options",
      type: `${PILLAR_CHOICE_REPORT_FILTER_CLASS}[]`,
      category: field.category,
      visibleIf: hasReportId,
    });
  }
};

type SurveyElementLike = {
  name?: string;
  elements?: unknown[];
  templateElements?: unknown[];
};

/**
 * Every copy of `elementName` inside the composite's content that a form's
 * questions are built from: the elements of a content panel, and the template
 * elements of a dynamic panel.
 *
 * A dynamic panel's rows are not walked. It clones its template for each row
 * added, and mirrors a later change to a template element onto the rows it has
 * already built (`onTemplateElementPropertyChanged`), so configuring the
 * template reaches the rows a form was loaded with as well.
 */
const findContentQuestions = (
  question: Question,
  elementName: string,
): Question[] => {
  const found: Question[] = [];
  const visited = new Set<unknown>();

  const visit = (element: unknown) => {
    if (!element || typeof element !== "object" || visited.has(element)) {
      return;
    }
    visited.add(element);

    const node = element as SurveyElementLike;
    if (node.name === elementName) {
      found.push(element as Question);
    }

    for (const children of [node.templateElements, node.elements]) {
      if (!Array.isArray(children)) {
        continue;
      }
      for (const child of children) {
        visit(child);
      }
    }
  };

  const content = question as unknown as {
    contentQuestion?: unknown;
    contentPanel?: unknown;
  };
  // A composite built from `questionJSON` holds a single content question, one
  // built from `elementsJSON` a content panel; neither defines the other.
  visit(content.contentQuestion);
  visit(content.contentPanel);

  return found;
};

/**
 * Rows are read back off the composite as whatever the serializer stored -
 * class instances when loaded from JSON, plain objects when a spec or a seed
 * wrote them - so they are flattened to what the dropdown reads.
 */
const toFilterRows = (value: unknown): CustomReportFilterRow[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const rows = value
    .map((row) => row as CustomReportFilterRow | null)
    .filter(
      (row): row is CustomReportFilterRow =>
        !!row?.filterKey && !!row?.questionName,
    )
    .map(({ filterKey, questionName }) => ({ filterKey, questionName }));

  return rows.length > 0 ? rows : undefined;
};

/**
 * Copies the composite's configured report settings onto the dropdown(s) it
 * wraps. Call from the composite's `onAfterRender`.
 *
 * Unset properties are copied through as `undefined` rather than skipped, so
 * clearing a report id in the builder puts the dropdown straight back on its
 * choice category instead of leaving the last id on the nested question.
 */
export const applyPillarChoiceCustomReportProperties = ({
  question,
  fields,
}: {
  question: Question;
  fields: PillarChoiceCustomReportField[];
}) => {
  for (const field of fields) {
    const names = pillarChoiceCustomReportPropertyNames(field);

    const values = {
      [DROPDOWN_PROPERTIES.reportId]: question.getPropertyValue(names.reportId),
      [DROPDOWN_PROPERTIES.labelPath]: question.getPropertyValue(
        names.labelPath,
      ),
      [DROPDOWN_PROPERTIES.valuePath]: question.getPropertyValue(
        names.valuePath,
      ),
      [DROPDOWN_PROPERTIES.filters]: toFilterRows(
        question.getPropertyValue(names.filters),
      ),
    };

    for (const target of findContentQuestions(question, field.elementName)) {
      for (const [name, value] of Object.entries(values)) {
        // Writing the same value back is a no-op in SurveyJS, so re-running on
        // every render does not loop through another render.
        target.setPropertyValue(name, value);
      }
    }
  }
};
